import { CvApiPayload } from '../../../domain/api/cv-api.service';
import {
  Apprenti,
  CvAnnotation,
  ExtractionMethod,
  ExtractionStatus,
  ExtractedContent,
  JourCours,
  Placement,
  StatutContrat,
  StatutContratWorkflow,
  StatutCv,
} from '../../../domain/models';
import { resolveProfileImageUrl } from '../../../core/utils/profile-image.format';
import { ApprentiInput } from './cvtheque.store';

const CV_STATUS_MAP: Record<string, StatutCv> = {
  'not-created': 'not-created',
  'in-progress': 'in-progress',
  valid: 'valid',
  'correction-pending': 'correction-pending',
  'correction-sent': 'correction-sent',
  en_attente: 'in-progress',
  valide: 'valid',
  correction_demandee: 'correction-pending',
  draft: 'in-progress',
  submitted: 'in-progress',
  validated: 'valid',
  rejected: 'correction-pending',
};

export function toApiPayload(
  input: Partial<ApprentiInput> | Partial<Apprenti>,
): CvApiPayload {
  return { ...input };
}

export function fromApiRecord(raw: unknown): Apprenti {
  const r = (raw as Record<string, unknown>) ?? {};
  const annotations = Array.isArray(r['annotations'])
    ? (r['annotations'] as Record<string, unknown>[]).map(mapAnnotation)
    : [];

  const { conseiller, conseillerId } = mapConseiller(r);

  return {
    id: String(r['id'] ?? r['_id'] ?? r['studentId'] ?? ''),
    lastname: String(r['lastname'] ?? r['nom'] ?? ''),
    firstname: String(r['firstname'] ?? r['prenom'] ?? ''),
    dateNaissance: parseDateNaissance(r['dateNaissance'] ?? r['birthdate']),
    formation: String(r['formation'] ?? r['title'] ?? '-'),
    jourCours: (r['jourCours'] as JourCours) ?? 'lundi',
    premiereAlternance: Boolean(r['premiereAlternance'] ?? false),
    placement: (r['placement'] as Placement) ?? 'non_place',
    statutContrat: mapStatutContrat(r['statutContrat']),
    dateDebutContrat: parseIsoDate(
      r['dateDebutContrat'] ?? r['contractStartDate'],
    ) ?? null,
    dateFinContrat: parseIsoDate(
      r['dateFinContrat'] ?? r['contractEndDate'],
    ) ?? null,
    statutContratWorkflow: mapStatutContratWorkflow(r['statutContratWorkflow']),
    statutCv: mapStatutCv(r),
    conseiller,
    conseillerId,
    archived: Boolean(r['archived'] ?? false),
    archiveReason: mapArchiveReason(r['archiveReason']),
    niveauEtude: r['niveauEtude'] != null ? String(r['niveauEtude']) : undefined,
    groupe: r['groupe'] != null ? String(r['groupe']) : undefined,
    statutInscription:
      r['statutInscription'] != null
        ? String(r['statutInscription'])
        : undefined,
    commentairesCount:
      typeof r['commentairesCount'] === 'number'
        ? r['commentairesCount']
        : undefined,
    telephone: r['telephone'] as string | undefined,
    cvFileName: String(
      r['cvFileName'] ?? r['filename'] ?? '',
    ) || undefined,
    cvFileUrl: resolveCvFileUrl(
      r['cvFileUrl'] ?? r['active_cv_file'] ?? r['fileUrl'],
    ),
    cvUploadedAt: parseIsoDate(r['cvUploadedAt'] ?? r['updatedAt']),
    extractionStatus: mapExtractionStatus(r['extractionStatus']),
    extractionError:
      r['extractionError'] != null && String(r['extractionError']).trim()
        ? String(r['extractionError'])
        : undefined,
    extractionMethod: mapExtractionMethod(r['extractionMethod']),
    extractedAt: parseIsoDate(r['extractedAt']),
    extractedText:
      r['extractedText'] != null && String(r['extractedText']).trim()
        ? String(r['extractedText'])
        : undefined,
    extractedContent: mapExtractedContent(r['extractedContent']),
    profileImageUrl: resolveProfileImageUrl(
      r['profileImage'] ?? r['photoUrl'] ?? r['photo'] ?? r['avatar'],
    ),
    annotations,
    canAssignCommercial: Boolean(r['canAssignCommercial']),
  };
}

function resolveCvFileUrl(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  const path = String(value).trim();
  if (!path) {
    return undefined;
  }
  if (/^blob:/i.test(path)) {
    return undefined;
  }
  if (/^https?:/i.test(path)) {
    return path;
  }
  return path;
}

function parseIsoDate(value: unknown): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }
  return String(value);
}

function mapArchiveReason(value: unknown): Apprenti['archiveReason'] {
  if (value == null || typeof value !== 'object') {
    return undefined;
  }

  const raw = value as Record<string, unknown>;
  const reason = raw['reason'] != null ? String(raw['reason']).trim() : '';
  if (!reason) {
    return undefined;
  }

  return {
    reason,
    commentaire:
      raw['commentaire'] != null && String(raw['commentaire']).trim()
        ? String(raw['commentaire'])
        : undefined,
    by: raw['by'] != null ? String(raw['by']) : undefined,
    on: raw['on'] != null ? String(raw['on']) : null,
  };
}

function mapAnnotation(raw: Record<string, unknown>): CvAnnotation {
  return {
    id: String(raw['id'] ?? raw['_id'] ?? ''),
    author: String(raw['author'] ?? ''),
    date: String(raw['date'] ?? ''),
    text: String(raw['text'] ?? ''),
  };
}

function mapConseiller(r: Record<string, unknown>): {
  conseiller: string;
  conseillerId?: string;
} {
  const raw =
    r['oldCommercial'] ?? r['conseillerAdministratif'] ?? r['conseiller'];
  if (raw != null && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const id = String(obj['_id'] ?? obj['id'] ?? '').trim();
    const fullname = String(obj['fullname'] ?? '').trim();
    const name =
      fullname ||
      [obj['firstname'], obj['lastname']].filter(Boolean).join(' ').trim();
    return {
      conseiller: name || '-',
      conseillerId: id || undefined,
    };
  }
  const name = raw == null || raw === '' ? '-' : String(raw);
  const explicitId = r['conseillerAdministratifId'] ?? r['conseillerId'];
  return {
    conseiller: name,
    conseillerId:
      explicitId != null && String(explicitId).trim()
        ? String(explicitId)
        : undefined,
  };
}

function mapStatutContrat(value: unknown): StatutContrat | null {
  if (value == null || value === '') {
    return null;
  }
  const raw = String(value).trim();
  if (raw === 'Actif' || raw === 'Résilié') {
    return raw;
  }
  return null;
}

const WORKFLOW_MAP: Record<string, StatutContratWorkflow> = {
  validée_par_employeur: 'validée_par_employeur',
  'validée par employeur': 'validée_par_employeur',
  signature_en_cours: 'signature_en_cours',
  'signature en cours': 'signature_en_cours',
  signé: 'signé',
  signe: 'signé',
  accordé: 'accordé',
  accorde: 'accordé',
  autre: 'autre',
};

function mapStatutContratWorkflow(
  value: unknown,
): StatutContratWorkflow | null {
  if (value == null || value === '') {
    return null;
  }
  const raw = String(value).trim().toLowerCase();
  return WORKFLOW_MAP[raw] ?? null;
}

function mapStatutCv(r: Record<string, unknown>): StatutCv {
  const raw = String(
    r['statutCv'] ?? r['statut'] ?? r['status'] ?? 'not-created',
  );
  return CV_STATUS_MAP[raw] ?? 'in-progress';
}

const EXTRACTION_STATUS_MAP: Record<string, ExtractionStatus> = {
  idle: 'idle',
  pending: 'pending',
  processing: 'processing',
  ready: 'ready',
  failed: 'failed',
};

function mapExtractionStatus(value: unknown): ExtractionStatus | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  const raw = String(value).trim();
  return EXTRACTION_STATUS_MAP[raw];
}

const EXTRACTION_METHOD_MAP: Record<string, ExtractionMethod> = {
  'pdf-text': 'pdf-text',
  docx: 'docx',
  ocr: 'ocr',
  hybrid: 'hybrid',
};

function mapExtractionMethod(value: unknown): ExtractionMethod | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  const raw = String(value).trim();
  return EXTRACTION_METHOD_MAP[raw];
}

function mapExtractedContent(value: unknown): ExtractedContent | undefined {
  if (value == null || typeof value !== 'object') {
    return undefined;
  }

  const content = value as Record<string, unknown>;
  const raw = content['raw'] as Record<string, unknown> | undefined;
  const rawText = raw?.['text'];
  if (rawText == null || !String(rawText).trim()) {
    return undefined;
  }

  return content as unknown as ExtractedContent;
}

function parseDateNaissance(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  return String(value);
}
